import {
  ArrowIcon,
  Box,
  Button,
  ErrorModal,
  Flex,
  Heading,
  ListItem,
  Text,
} from '@modusign/design-system';
import {
  convertAttachmentToPdf,
  convertDocumentToPdf,
  downloadAttachment,
  downloadDocumentFile,
  getDocument,
} from '@modusign/openapi-client-external-document';
import { AxiosError } from 'axios';
import { useMutation, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { Redirect, useHistory } from 'react-router';

import downloadIcon from '@app/assets/icons/icon-pdf-download-white.svg';
import DocumentHeaderDivider from '@app/components/DocumentHeaderDivider';
import DocumentZoomButton from '@app/components/DocumentZoomButton';
import Header from '@app/components/Header';
import HelpButton from '@app/components/HelpButton';
import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import PDFDocumentContentLayout from '@app/libs/reactPdfJs/components/PDFDocumentContentLayout';
import PDFDocumentPageLayout from '@app/libs/reactPdfJs/components/PDFDocumentPageLayout';
import PDFContainer from '@app/libs/reactPdfJs/containers/PDFContainer';
import { zoomIn, zoomOut } from '@app/libs/reactPdfJs/store/actions';
import { downloadFileFromURL } from '@app/store/transport-layer/files';

import ExternalDocumentHeaderMoreToolsDropdown from '../ExternalDocumentHeaderMoreToolsDropdown';
import FileOpenPasswordPrompt from './FileOpenPasswordPrompt';

interface FileViewerPageProps {
  documentId: string;
  id: string;
  isContractFile: boolean;
}

export default function FileViewerPage({ documentId, id, isContractFile }: FileViewerPageProps) {
  const workspaceId = useWorkspaceId();

  const dispatch = useDispatch();
  const history = useHistory();

  const {
    data: url,
    error,
    isLoading,
    isSuccess,
    refetch,
  } = useQuery<string, AxiosError>(
    ['externalDocumentFile', { id, isContractFile }],
    async () => {
      let url: string;
      if (isContractFile) {
        url = (await convertDocumentToPdf(workspaceId, documentId, id)).url;
      } else {
        url = (await convertAttachmentToPdf(workspaceId, documentId, id)).url;
      }

      return url;
    },
    { enabled: !!id, refetchOnWindowFocus: false, retry: false }
  );

  const handleZoomIn = () => dispatch(zoomIn());
  const handleZoomOut = () => dispatch(zoomOut());
  const { createErrorHandler } = useOpenErrorModal();

  const { mutate: handleDownload, isLoading: isDownloading } = useMutation(
    async () => {
      let url: string;
      let name: string;
      let extension: string;
      if (isContractFile) {
        [url, { name, extension }] = await Promise.all([
          downloadDocumentFile(workspaceId, documentId, id).then((data) => data.url),
          getDocument(workspaceId, documentId).then((doc) => doc.file),
        ]);
      } else {
        [url, { name, extension }] = await Promise.all([
          downloadAttachment(workspaceId, documentId, id).then((data) => data.url),
          getDocument(workspaceId, documentId).then(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            (doc) => doc.attachments.find((a) => a.id === id)!
          ),
        ]);
      }
      await downloadFileFromURL({ url, saveFileName: `${name}.${extension}` });
    },
    { onError: createErrorHandler({ title: '파일 다운로드 실패' }) }
  );

  if (error?.response?.status === 403) {
    return <Redirect to="/external-documents/list" />;
  }

  return (
    <PDFDocumentPageLayout bg="bodySecondaryBase" height="100vh" overflow="hidden" width="100vw">
      <Header alignItems="center" px={3}>
        <Flex flex={1} justifyContent="flex-start">
          <Flex alignItems="center" padding={[null, null, '10px']}>
            <Text
              as="button"
              color="textNeutralUltraLight"
              display="flex"
              fontSize="lg"
              fontWeight="bold"
              sx={{
                alignItems: 'center',
                '&:hover': {
                  color: 'textNeutralLightest',
                },
              }}
              type="button"
              onClick={history.goBack}
            >
              <ArrowIcon direction="left" height="18px" mr="10px" width="9px" /> 나가기
            </Text>
          </Flex>
        </Flex>
        <Flex alignItems="center" flex="1" justifyContent="flex-end">
          <ExternalDocumentHeaderMoreToolsDropdown
            menus={[
              {
                iconSrc: downloadIcon,
                text: '다운로드',
                onClick: () => handleDownload(),
              },
            ]}
          />
          <DocumentHeaderDivider />
          <DocumentZoomButton onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />
          <DocumentHeaderDivider />
          <HelpButton variant="darkIcon" />
        </Flex>
      </Header>
      <PDFDocumentContentLayout
        sx={{
          '.FormPreview-bar-container': {
            display: ['none', null, 'block'],
          },
        }}
      >
        {(isLoading || isDownloading) && <GlobalLoadingSpinner />}
        <ErrorModal
          closeButtonLabel="다시 시도"
          error={error}
          isOpen
          title="파일 불러오기 실패"
          onClose={() => refetch()}
        />
        {isSuccess &&
          (url ? (
            <PDFContainer
              PDFPasswordPrompt={FileOpenPasswordPrompt}
              src={url}
              thumbnailContainerClassName="FormPreview-bar-container"
            />
          ) : (
            <Box maxWidth="550px" mx="auto" p={4}>
              <Flex
                bg="darkGray700"
                borderRadius="12px"
                boxShadow="0px 10px 20px rgba(0, 0, 0, 0.25)"
                px={9}
                py={8}
              >
                <svg
                  fill="none"
                  height="35"
                  viewBox="0 0 35 35"
                  width="35"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="17.5" cy="17.5" r="14.5" stroke="#F33737" strokeWidth="2" />
                  <path
                    d="M23.6875 18.375H11.3125C10.6797 18.375 10.1875 17.8828 10.1875 17.25C10.1875 16.6523 10.6797 16.125 11.3125 16.125H23.6875C24.2852 16.125 24.8125 16.6523 24.8125 17.25C24.8125 17.8828 24.2852 18.375 23.6875 18.375Z"
                    fill="#F33737"
                  />
                </svg>
                <Flex flexDirection="column" ml={3}>
                  <Heading
                    as="h1"
                    color="textNeutralLightest"
                    fontSize="xl"
                    fontWeight="bold"
                    mb={6}
                  >
                    미리보기를 할 수 없는 파일입니다.
                  </Heading>
                  <ul>
                    <ListItem color="lightGray800" variant="circle">
                      미리보기를 지원하지 않는 파일 확장자일 경우
                    </ListItem>
                    <ListItem color="lightGray800" mt={2} variant="circle">
                      파일 확장자를 다른 확장자로 변경한 경우
                    </ListItem>
                    <ListItem color="lightGray800" mt={2} variant="circle">
                      지원 파일 : PDF (추후 다른 확장자도 지원될 예정입니다.)
                    </ListItem>
                  </ul>
                  <Button mt={8} variant="basic" width="102px" onClick={() => handleDownload()}>
                    다운로드
                  </Button>
                </Flex>
              </Flex>
            </Box>
          ))}
      </PDFDocumentContentLayout>
    </PDFDocumentPageLayout>
  );
}
